import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconEdit, IconListNumbers, IconPlus, IconTrash, IconUsers } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import {
  AddQueueMemberMutation,
  DeleteQueueMemberMutation,
  OrganizationMembersSubscription,
  QueueHoldMusicAudioFilesSubscription,
  QueueMembersSubscription,
  QueuesSubscription,
  UpdateQueueMemberMutation,
} from './-components/graphql';

type RoutingStrategy = 'longest_idle' | 'round_robin' | 'skills_based' | 'priority';

type OverflowAction = 'continue' | 'overflow_queue';

type DeliveryMode = 'pull' | 'push';

const ROUTING_STRATEGY_OPTIONS: Array<{ value: RoutingStrategy; label: string }> = [
  { value: 'longest_idle', label: 'Longest Idle' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'skills_based', label: 'Skills Based' },
  { value: 'priority', label: 'Priority' },
];

const OVERFLOW_ACTION_OPTIONS: Array<{ value: OverflowAction; label: string }> = [
  { value: 'continue', label: 'Return to Flow' },
  { value: 'overflow_queue', label: 'Overflow to Queue' },
];

const DELIVERY_MODE_OPTIONS: Array<{ value: DeliveryMode; label: string }> = [
  { value: 'pull', label: 'Pull' },
  { value: 'push', label: 'Push' },
];

const HOLD_MUSIC_SOURCE_OPTIONS = [
  { value: 'none', label: 'No hold music' },
  { value: 'catalog', label: 'Media library' },
  { value: 'custom_url', label: 'Custom URL' },
];

const STRATEGY_COLORS: Record<string, string> = {
  longest_idle: 'green',
  round_robin: 'blue',
  skills_based: 'violet',
  priority: 'orange',
};

type HoldMusicSource = 'none' | 'catalog' | 'custom_url';

type QueueForm = {
  name: string;
  routingStrategy: RoutingStrategy;
  maxWaitTimeSecs: number;
  overflowAction: OverflowAction;
  overflowQueueId: string | null;
  ringTimeoutSecs: number;
  deliveryMode: DeliveryMode;
  slaTargetSecs: number | null;
  holdMusicSource: HoldMusicSource;
  holdMusicUrl: string;
  holdMusicAudioFileId: string | null;
  announcementIntervalSecs: number | null;
  announcementMessage: string;
};

const EMPTY_FORM: QueueForm = {
  name: '',
  routingStrategy: 'longest_idle',
  maxWaitTimeSecs: 300,
  overflowAction: 'continue',
  overflowQueueId: null,
  ringTimeoutSecs: 30,
  deliveryMode: 'pull',
  slaTargetSecs: null,
  holdMusicSource: 'none',
  holdMusicUrl: '',
  holdMusicAudioFileId: null,
  announcementIntervalSecs: null,
  announcementMessage: '',
};

export const Route = createFileRoute('/_authed/settings/queues')({
  component: SettingsQueuesPage,
});

function overflowActionLabel(value: string) {
  return value === 'overflow_queue' ? 'overflow to queue' : 'return to flow';
}

function queueColumns(form: QueueForm) {
  return {
    name: form.name.trim(),
    routingStrategy: form.routingStrategy,
    maxWaitTimeSecs: form.maxWaitTimeSecs,
    overflowAction: form.overflowAction,
    overflowQueueId: form.overflowAction === 'overflow_queue' ? form.overflowQueueId : null,
    ringTimeoutSecs: form.ringTimeoutSecs,
    deliveryMode: form.deliveryMode,
    slaTargetSecs: form.slaTargetSecs,
    holdMusicUrl: form.holdMusicSource === 'custom_url' ? form.holdMusicUrl.trim() || null : null,
    holdMusicAudioFileId: form.holdMusicSource === 'catalog' ? form.holdMusicAudioFileId : null,
    announcementIntervalSecs: form.announcementIntervalSecs,
    announcementMessage: form.announcementMessage.trim() || null,
  };
}

function QueueMembersModal({
  organizationId,
  queueId,
  queueName,
  members,
  onClose,
}: {
  organizationId: string;
  queueId: string;
  queueName: string;
  members: Array<{ id: string; email: string; extensionNumber: string | null }>;
  onClose: () => void;
}) {
  const [{ data }] = useSubscription({ query: QueueMembersSubscription, variables: { queueId } });
  const [, addQueueMember] = useMutation(AddQueueMemberMutation);
  const [, updateQueueMember] = useMutation(UpdateQueueMemberMutation);
  const [, deleteQueueMember] = useMutation(DeleteQueueMemberMutation);

  const [addMemberId, setAddMemberId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assignedIds = new Set((data?.queueMembers ?? []).map((qm) => qm.memberId));

  const addMember = async () => {
    if (!addMemberId) return;

    setError(null);

    const result = await addQueueMember({ organizationId, queueId, memberId: addMemberId });

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to add the member'));

    setAddMemberId(null);
  };

  return (
    <Modal opened onClose={onClose} title={`Members — ${queueName}`} size="lg">
      <Stack gap="md">
        <ErrorAlert message={error} />

        <Group gap="xs">
          <Select
            placeholder="Add a member..."
            data={members
              .filter((member) => !assignedIds.has(member.id))
              .map((member) => ({
                value: member.id,
                label: member.extensionNumber ? `${member.email} (ext. ${member.extensionNumber})` : member.email,
              }))}
            value={addMemberId}
            onChange={setAddMemberId}
            searchable
            style={{ flex: 1 }}
          />
          <Button onClick={() => void addMember()} disabled={!addMemberId} leftSection={<IconPlus size={14} />}>
            Add
          </Button>
        </Group>

        {!data ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : data.queueMembers.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No members assigned to this queue yet.
          </Text>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Member</Table.Th>
                <Table.Th w={100}>Priority</Table.Th>
                <Table.Th w={90}>Escalation</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.queueMembers.map((queueMember) => (
                <Table.Tr key={queueMember.id} opacity={queueMember.isEscalation ? 0.7 : 1}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {queueMember.member.email}
                    </Text>
                    {queueMember.member.extensionNumber && (
                      <Text size="xs" c="dimmed">
                        ext. {queueMember.member.extensionNumber}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={queueMember.priority}
                      onChange={(value) =>
                        void updateQueueMember({
                          id: queueMember.id,
                          changes: { priority: typeof value === 'number' ? value : 0 },
                        })
                      }
                      size="xs"
                      min={-10}
                      max={10}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={queueMember.isEscalation ?? false}
                      onChange={(event) =>
                        void updateQueueMember({
                          id: queueMember.id,
                          changes: { isEscalation: event.currentTarget.checked },
                        })
                      }
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => void deleteQueueMember({ id: queueMember.id })}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
}

function audioFileOptions(
  audioFiles: ReadonlyArray<{ id: string; name: string }>,
  selected: { id: string; name: string } | null | undefined,
) {
  const options = audioFiles.map((audioFile) => ({ value: audioFile.id, label: audioFile.name }));

  if (!selected || options.some((option) => option.value === selected.id)) return options;

  return [...options, { value: selected.id, label: `${selected.name} (deleted)` }];
}

function QueueFormFields({
  form,
  otherQueues,
  audioFiles,
  onChange,
}: {
  form: QueueForm;
  otherQueues: Array<{ value: string; label: string }>;
  audioFiles: Array<{ value: string; label: string }>;
  onChange: (form: QueueForm) => void;
}) {
  return (
    <>
      <TextInput
        label="Queue Name"
        placeholder="e.g., Sales, Support"
        value={form.name}
        onChange={(event) => onChange({ ...form, name: event.currentTarget.value })}
        required
        data-autofocus
      />
      <Select
        label="Routing Strategy"
        data={ROUTING_STRATEGY_OPTIONS}
        value={form.routingStrategy}
        onChange={(value) => value && onChange({ ...form, routingStrategy: value as RoutingStrategy })}
        required
      />
      <Group grow>
        <NumberInput
          label="Max Wait Time (seconds)"
          value={form.maxWaitTimeSecs}
          onChange={(value) => onChange({ ...form, maxWaitTimeSecs: Number(value) || 300 })}
          min={10}
          required
        />
        <NumberInput
          label="Ring Timeout (seconds)"
          value={form.ringTimeoutSecs}
          onChange={(value) => onChange({ ...form, ringTimeoutSecs: Number(value) || 30 })}
          min={5}
          required
        />
      </Group>
      <Select
        label="Delivery Mode"
        description="Pull keeps callers in queue until an agent claims them. Push auto-offers callers to available agents."
        data={DELIVERY_MODE_OPTIONS}
        value={form.deliveryMode}
        onChange={(value) => value && onChange({ ...form, deliveryMode: value as DeliveryMode })}
        required
      />
      <NumberInput
        label="SLA Target (seconds)"
        description="Target time to answer calls"
        value={form.slaTargetSecs ?? ''}
        onChange={(value) => onChange({ ...form, slaTargetSecs: value === '' ? null : Number(value) })}
        min={1}
      />
      <Select
        label="Hold Music"
        description="Audio played while callers wait in queue"
        data={HOLD_MUSIC_SOURCE_OPTIONS}
        value={form.holdMusicSource}
        onChange={(value) => value && onChange({ ...form, holdMusicSource: value as HoldMusicSource })}
      />
      {form.holdMusicSource === 'catalog' && (
        <Select
          label="Hold Music File"
          placeholder={audioFiles.length > 0 ? 'Select audio media' : 'Upload audio in Settings → Media'}
          data={audioFiles}
          value={form.holdMusicAudioFileId}
          onChange={(value) => onChange({ ...form, holdMusicAudioFileId: value })}
          searchable
          clearable
        />
      )}
      {form.holdMusicSource === 'custom_url' && (
        <TextInput
          label="Hold Music URL"
          placeholder="https://example.com/hold-music.mp3"
          value={form.holdMusicUrl}
          onChange={(event) => onChange({ ...form, holdMusicUrl: event.currentTarget.value })}
        />
      )}
      <Select
        label="Overflow Behavior"
        description="When max wait is exceeded, either continue on the queue node's overflow handle or move the caller to another queue"
        data={OVERFLOW_ACTION_OPTIONS}
        value={form.overflowAction}
        onChange={(value) =>
          value &&
          onChange({
            ...form,
            overflowAction: value as OverflowAction,
            overflowQueueId: value === 'overflow_queue' ? form.overflowQueueId : null,
          })
        }
        required
      />
      {form.overflowAction === 'overflow_queue' && (
        <Select
          label="Overflow Queue"
          data={otherQueues}
          value={form.overflowQueueId}
          onChange={(value) => onChange({ ...form, overflowQueueId: value })}
          placeholder="Select a queue"
          required
        />
      )}
      <NumberInput
        label="Announcement Interval (seconds)"
        description="How often to announce queue position"
        value={form.announcementIntervalSecs ?? ''}
        onChange={(value) => onChange({ ...form, announcementIntervalSecs: value === '' ? null : Number(value) })}
        min={15}
      />
      <Textarea
        label="Announcement Message"
        description="Message to play periodically. Use {position} for queue position, {wait} for estimated wait."
        placeholder="You are caller number {position}. Estimated wait time is {wait} minutes."
        value={form.announcementMessage}
        onChange={(event) => onChange({ ...form, announcementMessage: event.currentTarget.value })}
        autosize
        minRows={2}
      />
    </>
  );
}

function Queues({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: QueuesSubscription, variables: { organizationId } });
  const [{ data: audioFileData }] = useSubscription({
    query: QueueHoldMusicAudioFilesSubscription,
    variables: { organizationId },
  });
  const [{ data: memberData }] = useSubscription({
    query: OrganizationMembersSubscription,
    variables: { organizationId },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [membersQueueId, setMembersQueueId] = useState<string | null>(null);
  const [form, setForm] = useState<QueueForm>({ ...EMPTY_FORM });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!data || !audioFileData) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEdit = (queue: (typeof data.queues)[number]) => {
    setEditingId(queue.id);
    setForm({
      name: queue.name,
      routingStrategy: queue.routingStrategy as RoutingStrategy,
      maxWaitTimeSecs: queue.maxWaitTimeSecs,
      overflowAction: queue.overflowAction === 'overflow_queue' ? 'overflow_queue' : 'continue',
      overflowQueueId: queue.overflowQueueId,
      ringTimeoutSecs: queue.ringTimeoutSecs,
      deliveryMode: (queue.deliveryMode ?? 'pull') as DeliveryMode,
      slaTargetSecs: queue.slaTargetSecs,
      holdMusicSource: queue.holdMusicAudioFileId ? 'catalog' : queue.holdMusicUrl ? 'custom_url' : 'none',
      holdMusicUrl: queue.holdMusicUrl ?? '',
      holdMusicAudioFileId: queue.holdMusicAudioFileId,
      announcementIntervalSecs: queue.announcementIntervalSecs,
      announcementMessage: queue.announcementMessage ?? '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    setIsSaving(true);
    setError(null);

    const columns = queueColumns(form);
    const { error: apiError } = editingId
      ? await api.queues({ id: editingId }).patch(columns)
      : await api.queues.post({ organizationId, enabled: true, ...columns });

    setIsSaving(false);

    if (apiError) return setError(getErrorMessage(apiError, 'Failed to save the queue'));

    closeModal();
  };

  const setEnabled = async (id: string, enabled: boolean) => {
    setError(null);

    const { error: apiError } = await api.queues({ id }).patch({ enabled });

    if (apiError) setError(getErrorMessage(apiError, 'Failed to save the queue'));
  };

  const remove = async (id: string) => {
    setError(null);

    const { error: apiError } = await api.queues({ id }).delete();

    setConfirmDeleteId(null);

    if (apiError) setError(getErrorMessage(apiError, 'Failed to delete the queue'));
  };

  const membersQueue = data.queues.find((queue) => queue.id === membersQueueId) ?? null;

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={2}>Queues</Title>
            <Text size="sm" c="dimmed" mt="2xs">
              Configure call queues and routing strategies for your contact center.
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Create Queue
          </Button>
        </Group>

        <ErrorAlert message={error} />

        {data.queues.length === 0 ? (
          <Paper p="3xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconListNumbers size={24} />
              </ThemeIcon>
              <div style={{ textAlign: 'center' }}>
                <Text fw={500}>No queues yet</Text>
                <Text size="sm" c="dimmed" mt="2xs">
                  Create a queue to start routing calls to your agents.
                </Text>
              </div>
              <Button variant="light" leftSection={<IconPlus size={16} />} onClick={openCreate}>
                Create Queue
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Strategy</Table.Th>
                  <Table.Th>Mode</Table.Th>
                  <Table.Th>Max Wait</Table.Th>
                  <Table.Th>Ring Timeout</Table.Th>
                  <Table.Th>Overflow</Table.Th>
                  <Table.Th>Enabled</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.queues.map((queue) => (
                  <Table.Tr key={queue.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {queue.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={STRATEGY_COLORS[queue.routingStrategy] ?? 'gray'}>
                        {queue.routingStrategy.replace(/_/g, ' ')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={queue.deliveryMode === 'push' ? 'green' : 'blue'}>
                        {queue.deliveryMode ?? 'pull'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {queue.maxWaitTimeSecs}s
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {queue.ringTimeoutSecs}s
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" tt="capitalize">
                        {overflowActionLabel(queue.overflowAction)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={queue.enabled}
                        onChange={(event) => void setEnabled(queue.id, event.currentTarget.checked)}
                        size="sm"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" color="indigo" onClick={() => setMembersQueueId(queue.id)}>
                          <IconUsers size={14} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" onClick={() => openEdit(queue)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        {confirmDeleteId === queue.id ? (
                          <Group gap={4}>
                            <Button size="compact-xs" variant="subtle" onClick={() => setConfirmDeleteId(null)}>
                              No
                            </Button>
                            <Button size="compact-xs" color="red" onClick={() => void remove(queue.id)}>
                              Yes
                            </Button>
                          </Group>
                        ) : (
                          <ActionIcon variant="subtle" color="red" onClick={() => setConfirmDeleteId(queue.id)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal opened={modalOpen} onClose={closeModal} title={editingId ? 'Edit Queue' : 'Create Queue'} size="lg">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Stack gap="md">
            <QueueFormFields
              form={form}
              otherQueues={data.queues
                .filter((queue) => queue.id !== editingId)
                .map((queue) => ({ value: queue.id, label: queue.name }))}
              audioFiles={audioFileOptions(
                audioFileData.audioFiles,
                data.queues.find((queue) => queue.id === editingId)?.holdMusicAudioFile,
              )}
              onChange={setForm}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" type="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" loading={isSaving} disabled={!form.name.trim()}>
                {editingId ? 'Save Changes' : 'Create Queue'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {membersQueue && (
        <QueueMembersModal
          organizationId={organizationId}
          queueId={membersQueue.id}
          queueName={membersQueue.name}
          members={(memberData?.organizationMembers ?? []).map((member) => ({
            id: member.id,
            email: member.email,
            extensionNumber: member.extensionNumber,
          }))}
          onClose={() => setMembersQueueId(null)}
        />
      )}
    </>
  );
}

function SettingsQueuesPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <Queues organizationId={organizationId} />;
}
